import { Grid, Typography, Paper, Box, Divider } from "@mui/material";
import React from "react";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

const BookingConfirmedDetails = () => {
  const location = useLocation();
  const bookingDetails = location.state?.bookingDetails;
  
  // Extract the relevant data from the response structure
  const ticketDetails = bookingDetails?.ticketDetails || {};
  const bookingInfo = bookingDetails?.bookingDetails || {};

  if (!bookingDetails) {
    return (
      <Box sx={{ padding: "2rem", marginTop: '4%', textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          No booking details found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "2rem", marginTop: '4%' }}>
      {/* Booking Summary */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ padding: "1.5rem", borderRadius: "8px" }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            Booking Confirmed
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Ticket Number: <strong>{ticketDetails.ticketNo}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            PNR: <strong>{ticketDetails.travelOperatorPNR}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Bus Operator: <strong>{ticketDetails.travelName}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Bus Type: <strong>{ticketDetails.busType}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Journey:{" "}
            <strong>
              {ticketDetails.origin} → {ticketDetails.destination}
            </strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Date: <strong>{new Date(ticketDetails.dateOfJourney).toLocaleDateString()}</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Departure:{" "}
            <strong>
              {new Date(ticketDetails.departureTime).toLocaleTimeString()}
            </strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Arrival:{" "}
            <strong>
              {new Date(ticketDetails.arrivalTime).toLocaleTimeString()}
            </strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Status: <strong>{bookingInfo.bookingStatus || "Confirmed"}</strong>
          </Typography>
        </Paper>
      </Grid>

      {/* Passenger and Boarding Details */}
      <Grid sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: '2%', 
        marginBottom: '2%', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 2 
      }}>
        {/* Passenger Details */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "1.5rem", borderRadius: "8px", height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Passenger Details
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">
                Name: <strong>{ticketDetails.passengerName}</strong>
              </Typography>
              <Typography variant="body1">
                Seat Number: <strong>{ticketDetails.seatName}</strong>
              </Typography>
              <Typography variant="body1">
                Seat Type: <strong>{ticketDetails.seatType === "seat" ? "Seater" : "Sleeper"}</strong>
              </Typography>
              <Typography variant="body1">
                Contact: <strong>{ticketDetails.contactNo}</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Boarding Point Details */}
        <Grid item xs={12} md={5}>
          <Paper elevation={3} sx={{ padding: "1.5rem", borderRadius: "8px", height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Boarding Point
            </Typography>
            {bookingDetails.selectedBoardingPoint ? (
              <>
                <Typography variant="body1">
                  Location: <strong>{bookingDetails.selectedBoardingPoint.CityPointLocation}</strong>
                </Typography>
                <Typography variant="body1">
                  Address: <strong>{bookingDetails.selectedBoardingPoint.CityPointAddress || "NA"}</strong>
                </Typography>
                <Typography variant="body1">
                  Landmark: <strong>{bookingDetails.selectedBoardingPoint.CityPointLandmark || "NA"}</strong>
                </Typography>
                <Typography variant="body1">
                  Time:{" "}
                  <strong>
                    {new Date(bookingDetails.selectedBoardingPoint.CityPointTime).toLocaleTimeString()}
                  </strong>
                </Typography>
                <Typography variant="body1">
                  Contact: <strong>{bookingDetails.selectedBoardingPoint.CityPointContactNumber || "NA"}</strong>
                </Typography>
              </>
            ) : (
              <Typography variant="body1">Boarding point information not available</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Price and Invoice Details */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ padding: "1.5rem", borderRadius: "8px" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            Price Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">
                Total Amount:{" "}
                <strong>
                  ₹ {ticketDetails.invoiceAmount}
                </strong>
              </Typography>
              <Typography variant="body1">
                Number of Seats:{" "}
                <strong>
                  {ticketDetails.noOfSeats}
                </strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">
                Invoice Number: <strong>{ticketDetails.invoiceNo}</strong>
              </Typography>
              <Typography variant="body1">
                Invoice Date:{" "}
                <strong>
                  {new Date(ticketDetails.invoiceCreatedOn).toLocaleString()}
                </strong>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Important Information */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Paper elevation={3} sx={{ padding: "1.5rem", borderRadius: "8px" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            Important Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • Please arrive at the boarding point at least 30 minutes before departure
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • Carry a printed or mobile copy of your ticket and valid ID proof
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • Boarding point contact: {ticketDetails.contactNo}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • Trace ID for reference: {ticketDetails.traceId}
          </Typography>
        </Paper>
      </Grid>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
      />
    </Box>
  );
};

export default BookingConfirmedDetails;